import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search, Phone, Mail, Package, Plus, Edit, Trash2, FileText, ShoppingCart, ExternalLink, Eye, MapPin, RefreshCw, Trash, CheckCircle2, Grid3x3, List, Download, MessageCircle, Send, AlertCircle } from "lucide-react";
import { ImportContacts } from "@/components/ImportContacts";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { NovoClienteModal } from "@/components/NovoClienteModal";
import { EditClienteModal } from "@/components/EditClienteModal";
import { PageHeader } from "@/components/PageHeader";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { createCustomer, deleteCustomer, updateCustomer, deleteInvalidCustomers } from "@/integrations/supabase/customers";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSync } from "@/contexts/SyncContext";
import { useSyncOperations } from "@/hooks/useSyncOperations";
import { validateName, validatePhone, validateEmail, validateForm, validateOptionalCpfCnpjField } from "@/utils/validators";
import { validateCpfCnpj, formatCpfCnpj } from "@/utils/cpfCnpjValidation";
import { errorHandler } from "@/utils/errorHandler";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CustomerRow } from "@/integrations/supabase/customers";
import { getOnboardingProgress, completeOnboardingStep } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";
import { useAuth } from "@/components/AuthProvider";
import { PermissionGuard } from "@/components/PermissionGuard";
import { MedidasClienteCard } from "@/components/MedidasClienteCard";
import { usePermission } from "@/hooks/usePermission";
import { obterProvasDoCliente, ProvaAgendada } from "@/integrations/supabase/prova-agendamentos";
import { Calendar, Clock } from "lucide-react";

type ClientWithHistory = CustomerRow & {
  orders?: number;
  quotes?: number;
  lastOrder?: string | null;
  lastQuote?: string | null;
  totalValue?: number;
  type?: string;
  ordersList?: unknown[];
  quotesList?: unknown[];
};

export default function Clientes() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { empresa } = useAuth();
  const { invalidateRelated } = useSync();
  const { syncAfterCreate, syncAfterUpdate, syncAfterDelete, syncWithToast } = useSyncOperations();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"recentes" | "antigos" | "az">("recentes");
  const [isNovoClienteModalOpen, setIsNovoClienteModalOpen] = useState(false);
  const [itemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<"cards" | "tabela">("cards");
  const [filterType, setFilterType] = useState<"todos" | "vip" | "regular">("todos");
  const [filterStatus, setFilterStatus] = useState<"todos" | "com-pedidos" | "sem-pedidos" | "sem-medidas">("todos");

  // Query para verificar progresso do onboarding
  const { data: onboardingProgress } = useQuery({
    queryKey: ['onboarding-progress', empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingClient, setViewingClient] = useState<ClientWithHistory | null>(null);
  const [editingClient, setEditingClient] = useState<ClientWithHistory | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    email: "",
    data_nascimento: "",
    cpf_cnpj: "",
    inscricao_estadual: "",
    ie_isento: false,
    endereco_logradouro: "",
    endereco_numero: "",
    endereco_complemento: "",
    endereco_bairro: "",
    endereco_cidade: "",
    endereco_uf: "",
    endereco_cep: ""
  });

  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  const [duplicates, setDuplicates] = useState<Array<{ type: string; clients: ClientWithHistory[] }> | null>(null);
  const [isDuplicatesDialogOpen, setIsDuplicatesDialogOpen] = useState(false);

  // Estados para modal de pedidos/orçamentos do cliente
  const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
  const [isQuotesModalOpen, setIsQuotesModalOpen] = useState(false);
  const [selectedClientForOrders, setSelectedClientForOrders] = useState<ClientWithHistory | null>(null);
  const [selectedClientForQuotes, setSelectedClientForQuotes] = useState<ClientWithHistory | null>(null);
  const [clientOrders, setClientOrders] = useState<any[]>([]);
  const [clientQuotes, setClientQuotes] = useState<any[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [clientNotFoundName, setClientNotFoundName] = useState<string | null>(null);
  const [isClientNotFoundDialogOpen, setIsClientNotFoundDialogOpen] = useState(false);
  const [provasCliente, setProvasCliente] = useState<ProvaAgendada[]>([]);
  const [isLoadingProvas, setIsLoadingProvas] = useState(false);

  const findDuplicates = () => {
    const duplicateGroups: { [key: string]: ClientWithHistory[] } = {};
    const cpfjCnpjMap: { [key: string]: ClientWithHistory[] } = {};
    const emailMap: { [key: string]: ClientWithHistory[] } = {};
    const nameMap: { [key: string]: ClientWithHistory[] } = {};

    // Agrupar por CPF/CNPJ
    clients.forEach(client => {
      const cpfCnpj = (client as any).cpf_cnpj;
      if (cpfCnpj) {
        if (!cpfjCnpjMap[cpfCnpj]) {
          cpfjCnpjMap[cpfCnpj] = [];
        }
        cpfjCnpjMap[cpfCnpj].push(client);
      }
    });

    // Agrupar por Email
    clients.forEach(client => {
      const email = client.email;
      if (email) {
        if (!emailMap[email]) {
          emailMap[email] = [];
        }
        emailMap[email].push(client);
      }
    });

    // Agrupar por Nome (case-insensitive)
    clients.forEach(client => {
      const name = client.name?.toLowerCase().trim();
      if (name) {
        if (!nameMap[name]) {
          nameMap[name] = [];
        }
        nameMap[name].push(client);
      }
    });

    const result: Array<{ type: string; clients: ClientWithHistory[] }> = [];

    // Adicionar CPF/CNPJ duplicados
    Object.entries(cpfjCnpjMap).forEach(([key, group]) => {
      if (group.length > 1) {
        result.push({ type: `CPF/CNPJ: ${key}`, clients: group });
      }
    });

    // Adicionar Email duplicados
    Object.entries(emailMap).forEach(([key, group]) => {
      if (group.length > 1) {
        result.push({ type: `Email: ${key}`, clients: group });
      }
    });

    // Adicionar Nome duplicados
    Object.entries(nameMap).forEach(([key, group]) => {
      if (group.length > 1) {
        result.push({ type: `Nome: ${key.charAt(0).toUpperCase() + key.slice(1)}`, clients: group });
      }
    });

    if (result.length === 0) {
      toast.success("✅ Nenhum duplicado encontrado!");
      return;
    }

    setDuplicates(result);
    setIsDuplicatesDialogOpen(true);
  };

  const handleSelectClient = (clientId: string) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedClients.size === clients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(clients.map(c => c.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir ${selectedClients.size} cliente(s)? Esta ação não pode ser desfeita.`)) {
      return;
    }

    setIsDeletingSelected(true);
    let deletedCount = 0;
    let errorCount = 0;

    for (const clientId of selectedClients) {
      try {
        const result = await deleteCustomer(clientId);
        if (result.ok) {
          deletedCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    setIsDeletingSelected(false);
    setSelectedClients(new Set());

    if (deletedCount > 0) {
      toast.success(`✅ ${deletedCount} cliente(s) excluído(s) com sucesso!`);
      refetch();
    }
    if (errorCount > 0) {
      toast.error(`❌ Erro ao excluir ${errorCount} cliente(s)`);
    }
  };

  const handleAutoCleanupDuplicates = async () => {
    if (!duplicates || duplicates.length === 0) {
      toast.error("Nenhum duplicado para limpar");
      return;
    }

    const confirmMessage = `Tem certeza que deseja remover os clientes duplicados?\n\nSerá mantido apenas 1 cliente de cada grupo de duplicados.\n\nEsta ação não pode ser desfeita.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeletingSelected(true);
    let deletedCount = 0;
    let errorCount = 0;

    // Para cada grupo de duplicados
    for (const group of duplicates) {
      const clientsInGroup = group.clients;

      // Manter o cliente mais antigo (primeiro na lista), deletar os outros
      if (clientsInGroup.length > 1) {
        const clientsToDelete = clientsInGroup.slice(1); // Todos exceto o primeiro

        for (const client of clientsToDelete) {
          try {
            console.log(`🗑️ Deletando cliente duplicado: ${client.name} (ID: ${client.id})`);
            const result = await deleteCustomer(client.id);
            if (result.ok) {
              deletedCount++;
            } else {
              console.error(`Erro ao deletar ${client.name}:`, result.error);
              errorCount++;
            }
          } catch (error) {
            console.error(`Erro ao deletar cliente:`, error);
            errorCount++;
          }
        }
      }
    }

    setIsDeletingSelected(false);
    setDuplicates(null);
    setIsDuplicatesDialogOpen(false);
    setSelectedClients(new Set());

    if (deletedCount > 0) {
      toast.success(`✅ ${deletedCount} cliente(s) duplicado(s) removido(s)!`);
      refetch();
    }
    if (errorCount > 0) {
      toast.error(`❌ Erro ao remover ${errorCount} cliente(s)`);
    }
  };

  const handleViewClient = (client: ClientWithHistory) => {
    setViewingClient(client);
    setIsViewDialogOpen(true);
  };

  const handleEditClient = (client: ClientWithHistory) => {
    setEditingClient(client);
    setEditForm({
      name: client.name || "",
      phone: client.phone || "",
      email: client.email || "",
      data_nascimento: (client as any).data_nascimento ? (client as any).data_nascimento.split('T')[0] : "",
      cpf_cnpj: (client as any).cpf_cnpj || "",
      inscricao_estadual: (client as any).inscricao_estadual || "",
      ie_isento: !!(client as any).ie_isento,
      endereco_logradouro: (client as any).endereco_logradouro || "",
      endereco_numero: (client as any).endereco_numero || "",
      endereco_complemento: (client as any).endereco_complemento || "",
      endereco_bairro: (client as any).endereco_bairro || "",
      endereco_cidade: (client as any).endereco_cidade || "",
      endereco_uf: (client as any).endereco_uf || "",
      endereco_cep: (client as any).endereco_cep || ""
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingClient) return;
    
    // Se for um cliente de demonstração, simular sucesso
    if (editingClient.id.startsWith('demo-')) {
      console.log("📝 Editando cliente de demonstração:", editingClient.name);
      console.log("📝 Novos dados:", editForm);
      
      // Simular delay de salvamento
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast.success(`Cliente "${editForm.name}" atualizado com sucesso! (Modo demonstração)`);
      setIsEditDialogOpen(false);
      setEditingClient(null);
      return;
    }
    
    // Para clientes reais, salvar no banco
    try {
      // Preparar dados para atualização
      const updateData: {
        name: string;
        phone: string;
        email?: string;
        data_nascimento?: string | null;
        cpf_cnpj?: string;
        inscricao_estadual?: string;
        ie_isento?: boolean;
        endereco_logradouro?: string;
        endereco_numero?: string;
        endereco_complemento?: string;
        endereco_bairro?: string;
        endereco_cidade?: string;
        endereco_uf?: string;
        endereco_cep?: string;
      } = {
        name: editForm.name.trim(),
        phone: editForm.phone.trim()
      };

      // Adicionar campos opcionais apenas se preenchidos
      if (editForm.email && editForm.email.trim()) {
        updateData.email = editForm.email.trim();
      }
      if (editForm.data_nascimento && editForm.data_nascimento.trim()) {
        updateData.data_nascimento = editForm.data_nascimento;
      } else {
        updateData.data_nascimento = null;
      }
      if (editForm.cpf_cnpj && editForm.cpf_cnpj.trim()) {
        updateData.cpf_cnpj = editForm.cpf_cnpj.trim();
      }
      if (editForm.inscricao_estadual && editForm.inscricao_estadual.trim() && editForm.cpf_cnpj.replace(/\D/g, '').length === 14 && !editForm.ie_isento) {
        updateData.inscricao_estadual = editForm.inscricao_estadual.trim();
      } else {
        updateData.inscricao_estadual = null;
      }
      if (editForm.ie_isento && editForm.cpf_cnpj.replace(/\D/g, '').length === 14) {
        updateData.ie_isento = true;
        updateData.inscricao_estadual = null;
      } else {
        updateData.ie_isento = false;
      }

      if (editForm.endereco_logradouro && editForm.endereco_logradouro.trim()) {
        updateData.endereco_logradouro = editForm.endereco_logradouro.trim();
      }

      if (editForm.endereco_numero && editForm.endereco_numero.trim()) {
        updateData.endereco_numero = editForm.endereco_numero.trim();
      }

      if (editForm.endereco_complemento && editForm.endereco_complemento.trim()) {
        updateData.endereco_complemento = editForm.endereco_complemento.trim();
      }

      if (editForm.endereco_bairro && editForm.endereco_bairro.trim()) {
        updateData.endereco_bairro = editForm.endereco_bairro.trim();
      }

      if (editForm.endereco_cidade && editForm.endereco_cidade.trim()) {
        updateData.endereco_cidade = editForm.endereco_cidade.trim();
      }

      if (editForm.endereco_uf && editForm.endereco_uf.trim()) {
        updateData.endereco_uf = editForm.endereco_uf.trim().toUpperCase();
      }

      if (editForm.endereco_cep && editForm.endereco_cep.trim()) {
        updateData.endereco_cep = editForm.endereco_cep.trim();
      }
      
      console.log("📝 Dados preparados para atualização:", updateData);
      
      const result = await updateCustomer(editingClient.id, updateData);
      
      if (result.ok) {
        toast.success("Cliente atualizado com sucesso!");
        setIsEditDialogOpen(false);
        setEditingClient(null);
        // Limpar completamente o cache e refetch
        queryClient.removeQueries({ queryKey: ["customers"] });
        queryClient.invalidateQueries({ queryKey: ["customers"] });
        await refetch();
        // Sincronização automática
        syncAfterUpdate('customers', editingClient.id, result.data);
      } else {
        console.error("Erro ao atualizar cliente:", result.error);
        toast.error(result.error || "Erro ao atualizar cliente");
      }
    } catch (error) {
      console.error("Erro ao atualizar cliente:", error);
      toast.error("Erro ao atualizar cliente");
    }
  };

  const handleCleanInvalidCustomers = async () => {
    const confirmMessage = "Tem certeza que deseja excluir todos os clientes com nomes inválidos (BR, AO, AR, etc.)?\n\nEsta ação não pode ser desfeita!";
    if (!confirm(confirmMessage)) {
      return;
    }
    
    try {
      toast.loading("Limpando clientes inválidos...", { id: "clean-invalid" });
      const result = await deleteInvalidCustomers();
      
      if (result.ok) {
        toast.success(`✅ ${result.deletedCount || 0} clientes inválidos excluídos com sucesso!`, { id: "clean-invalid" });
        // Limpar cache e refetch
        queryClient.removeQueries({ queryKey: ["customers"] });
        queryClient.invalidateQueries({ queryKey: ["customers"] });
        await refetch();
      } else {
        toast.error(result.error || "Erro ao limpar clientes inválidos", { id: "clean-invalid" });
      }
    } catch (error) {
      console.error("Erro ao limpar clientes inválidos:", error);
      toast.error("Erro ao limpar clientes inválidos", { id: "clean-invalid" });
    }
  };

  const handleDeleteClient = async (client: ClientWithHistory) => {
    const clientName = client.name || "este cliente";
    if (confirm(`Tem certeza que deseja excluir "${clientName}"?`)) {
      // Se for um cliente de demonstração, simular exclusão
      if (client.id.startsWith('demo-')) {
        console.log("🗑️ Excluindo cliente de demonstração:", clientName);
        
        // Simular delay de exclusão
        await new Promise(resolve => setTimeout(resolve, 300));
        
        toast.success(`Cliente "${clientName}" excluído com sucesso! (Modo demonstração)`);
        return;
      }
      
      // Para clientes reais, excluir do banco
      try {
        console.log("🗑️ Excluindo cliente real do banco:", client.id);
        
        const result = await deleteCustomer(client.id);
        if (result.ok) {
          toast.success("Cliente excluído com sucesso!");
          // Limpar completamente o cache e refetch
          queryClient.removeQueries({ queryKey: ["customers"] });
          queryClient.invalidateQueries({ queryKey: ["customers"] });
          await refetch();
          // Sincronização automática
          syncAfterDelete('customers', client.id);
        } else {
          toast.error(result.error || "Erro ao excluir cliente");
        }
      } catch (error) {
        console.error("Erro ao excluir cliente:", error);
        toast.error("Erro ao excluir cliente");
      }
    }
  };

  // Função para criar cliente (modo real)
  const createCustomerReal = async (data: {
    name: string;
    phone: string;
    email: string;
    data_nascimento?: string | null;
    cpf_cnpj?: string;
    address?: string;
    endereco_logradouro?: string;
    endereco_numero?: string;
    endereco_complemento?: string;
    endereco_bairro?: string;
    endereco_cidade?: string;
    endereco_uf?: string;
    endereco_cep?: string;
  }) => {
    console.log("➕ Criando cliente real no banco:", data);
    
    try {
      const result = await createCustomer(data);
      return result;
    } catch (error) {
      console.error("Erro ao criar cliente:", error);
      return { ok: false, error: "Erro ao criar cliente" };
    }
  };

  // Dados de demonstração (sempre funcionam)
  const demoClients: ClientWithHistory[] = [
    {
      id: "demo-1",
      name: "Maria Silva",
      phone: "(11) 98765-4321",
      email: "maria.silva@email.com",
      address: "Rua das Flores, 123",
      orders: 8,
      lastOrder: "2025-10-12",
      type: "VIP",
    },
    {
      id: "demo-2", 
      name: "João Santos",
      phone: "(11) 97654-3210",
      email: "joao.santos@email.com",
      address: "Av. Principal, 456",
      orders: 3,
      lastOrder: "2025-10-10",
      type: "Regular",
    },
    {
      id: "demo-3",
      name: "Ana Costa",
      phone: "(11) 96543-2109",
      email: "ana.costa@email.com",
      address: "Rua da Paz, 789",
      orders: 5,
      lastOrder: "2025-10-15",
      type: "VIP",
    },
    {
      id: "demo-4",
      name: "Carlos Oliveira",
      phone: "(11) 95432-1098",
      email: "carlos.oliveira@email.com",
      address: "Rua do Comércio, 321",
      orders: 2,
      lastOrder: "2025-10-08",
      type: "Regular",
    }
  ];

  // Buscar clientes reais do banco de dados com histórico
  const { data: realClients = [], isLoading, refetch, error: queryError } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      try {
        window.console.log("🔍 [CLIENTES] ===== INICIANDO BUSCA =====");
        window.console.log("🔍 [CLIENTES] Iniciando busca de clientes do banco de dados...");
        
        // Obter usuário logado
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          window.console.error("❌ [CLIENTES] Erro ao obter usuário:", userError);
          return [];
        }
        
        window.console.log("✅ [CLIENTES] Usuário logado:", user.id);
        
        // Obter empresa_id do usuário logado
        const { data: userEmpresa, error: userEmpresaError } = await supabase
          .from("user_empresas")
          .select("empresa_id")
          .eq("user_id", user.id)
          .single();
        
        window.console.log("🔍 [CLIENTES] Resultado user_empresas:", { 
          userEmpresa, 
          userEmpresaError,
          empresa_id: userEmpresa?.empresa_id 
        });
        
        if (userEmpresaError || !userEmpresa?.empresa_id) {
          window.console.error("❌ [CLIENTES] Usuário não tem empresa associada:", userEmpresaError);
          return [];
        }
        
        window.console.log("✅ [CLIENTES] Empresa ID encontrada:", userEmpresa.empresa_id);
        
        // Buscar TODOS os clientes da empresa (sem filtros adicionais)
        // IMPORTANTE: Não usar .limit() para garantir que todos sejam retornados
        const { data: customers, error: customersError } = await supabase
          .from("customers")
          .select("*")
          .eq("empresa_id", userEmpresa.empresa_id)
          .order("name", { ascending: true });
        
        window.console.log("🔍 [CLIENTES] Resultado consulta customers:", { 
          count: customers?.length || 0, 
          error: customersError,
          empresa_id_usada: userEmpresa.empresa_id,
          clientes_ids: customers?.map(c => ({ id: c.id, name: c.name, empresa_id: c.empresa_id }))
        });
        
        // Log detalhado de cada cliente para debug
        if (customers && customers.length > 0) {
          window.console.log("📋 [CLIENTES] Dados brutos dos clientes:", customers.map(c => ({
            id: c.id,
            name: c.name,
            name_length: c.name?.length || 0,
            name_type: typeof c.name,
            name_raw: JSON.stringify(c.name),
            phone: c.phone,
            email: c.email
          })));
        }
        
        if (customersError) {
          window.console.error("❌ [CLIENTES] Erro ao buscar clientes:", customersError);
          window.console.error("❌ [CLIENTES] Detalhes do erro:", JSON.stringify(customersError, null, 2));
          return [];
        }
        
        if (!customers || customers.length === 0) {
          window.console.warn("⚠️ [CLIENTES] Nenhum cliente encontrado no banco para empresa_id:", userEmpresa.empresa_id);
          return [];
        }
        
        window.console.log(`✅ [CLIENTES] ${customers.length} clientes encontrados no banco`);
        window.console.log(`✅ [CLIENTES] Nomes dos clientes:`, customers.map(c => c.name || "Sem nome"));
        
        // Parse JSON fields from database
        const parsedCustomers = customers.map(client => ({
          ...client,
          telefones: typeof client.telefones === 'string' ?
            (client.telefones ? JSON.parse(client.telefones) : []) :
            (Array.isArray(client.telefones) ? client.telefones : []),
          contatos: typeof client.contatos === 'string' ?
            (client.contatos ? JSON.parse(client.contatos) : []) :
            (Array.isArray(client.contatos) ? client.contatos : []),
        }));

        // Filtrar clientes com nomes válidos (2+ caracteres e não são códigos de país)
        const validClients = parsedCustomers.filter(client => {
          const name = client.name?.trim() || "";
          // Filtrar nomes muito curtos (provavelmente dados corrompidos)
          // Códigos de país comuns: BR, AO, AR, BD, CO, CR, IN, MX, MZ, NI, PK, PT, TM
          const countryCodes = ['BR', 'AO', 'AR', 'BD', 'CO', 'CR', 'IN', 'MX', 'MZ', 'NI', 'PK', 'PT', 'TM'];
          return name.length >= 2 && !countryCodes.includes(name.toUpperCase());
        });

        const invalidClients = customers.filter(client => {
          const name = client.name?.trim() || "";
          const countryCodes = ['BR', 'AO', 'AR', 'BD', 'CO', 'CR', 'IN', 'MX', 'MZ', 'NI', 'PK', 'PT', 'TM'];
          return name.length < 2 || countryCodes.includes(name.toUpperCase());
        });
        
        if (invalidClients.length > 0) {
          window.console.warn(`⚠️ [CLIENTES] ${invalidClients.length} clientes com nomes inválidos detectados (serão exibidos sem histórico)`);
        }
        
        // Buscar TODOS os pedidos e orçamentos de uma vez (otimização)
        const allCustomerNames = validClients.map(c => c.name).filter(Boolean);
        
        // Buscar todos os pedidos de uma vez
        const { data: allOrders } = await (supabase
          .from("atelie_orders" as any)
          .select("code, value, paid, status, delivery_date, created_at, customer_name")
          .eq("empresa_id", userEmpresa.empresa_id)
          .in("customer_name", allCustomerNames)
          .order("created_at", { ascending: false }) as any);
        
        // Buscar todos os orçamentos de uma vez
        const { data: allQuotes } = await (supabase
          .from("atelie_quotes" as any)
          .select("code, total_value, status, date, created_at, customer_name")
          .eq("empresa_id", userEmpresa.empresa_id)
          .in("customer_name", allCustomerNames)
          .order("created_at", { ascending: false }) as any);
        
        // Agrupar pedidos e orçamentos por cliente
        const ordersByCustomer = new Map<string, any[]>();
        const quotesByCustomer = new Map<string, any[]>();
        
        (allOrders || []).forEach((order: any) => {
          if (order.customer_name) {
            if (!ordersByCustomer.has(order.customer_name)) {
              ordersByCustomer.set(order.customer_name, []);
            }
            ordersByCustomer.get(order.customer_name)!.push(order);
          }
        });
        
        (allQuotes || []).forEach((quote: any) => {
          if (quote.customer_name) {
            if (!quotesByCustomer.has(quote.customer_name)) {
              quotesByCustomer.set(quote.customer_name, []);
            }
            quotesByCustomer.get(quote.customer_name)!.push(quote);
          }
        });
        
        // Processar clientes válidos com histórico
        const clientsWithHistory = validClients.map((client) => {
          const orders = ordersByCustomer.get(client.name) || [];
          const quotes = quotesByCustomer.get(client.name) || [];
          
          const totalOrders = orders.length;
          const totalQuotes = quotes.length;
          const lastOrderDate = orders[0]?.created_at ? 
            new Date(orders[0].created_at).toISOString().split('T')[0] : null;
          const lastQuoteDate = quotes[0]?.created_at ? 
            new Date(quotes[0].created_at).toISOString().split('T')[0] : null;
          
          const totalValue = orders.reduce((sum, order) => sum + (order.value || 0), 0);
          const type = totalValue > 1000 ? "VIP" : "Regular";
          
          return {
            ...client,
            orders: totalOrders,
            quotes: totalQuotes,
            lastOrder: lastOrderDate,
            lastQuote: lastQuoteDate,
            totalValue: totalValue,
            type: type,
            ordersList: orders,
            quotesList: quotes
          };
        });
        
        // Adicionar clientes inválidos sem histórico (para que possam ser editados)
        const invalidClientsWithoutHistory = invalidClients.map(client => ({
          ...client,
          orders: 0,
          quotes: 0,
          lastOrder: null,
          lastQuote: null,
          totalValue: 0,
          type: "Regular",
          ordersList: [],
          quotesList: []
        }));
        
        // Combinar clientes válidos e inválidos
        const allClientsWithHistory = [...clientsWithHistory, ...invalidClientsWithoutHistory];

        console.log(`✅ [CLIENTES] Processamento concluído. Retornando ${allClientsWithHistory.length} clientes (${clientsWithHistory.length} válidos + ${invalidClientsWithoutHistory.length} inválidos)`);
        return allClientsWithHistory;
      } catch (error) {
        console.error("❌ [CLIENTES] Erro ao buscar clientes:", error);
        console.warn("⚠️ [CLIENTES] Retornando array vazio, será usado dados de demonstração");
        return [];
      }
    },
    select: (rows) =>
      rows.map((client: ClientWithHistory) => {
        const rawName = client.name ?? (client as any).customer_name ?? (client as any).client ?? "";
        const normalizedName = typeof rawName === "string" ? rawName.trim() : "";

        return {
          ...client,
          name: normalizedName || "Cliente sem nome",
          orders: client.orders ?? 0,
          quotes: client.quotes ?? 0,
          lastOrder: client.lastOrder ?? null,
          lastQuote: client.lastQuote ?? null,
          totalValue: client.totalValue ?? 0,
          type: client.type ?? (client.totalValue && client.totalValue > 1000 ? "VIP" : "Regular"),
          ordersList: client.ordersList ?? [],
          quotesList: client.quotesList ?? [],
          telefones: typeof (client as any).telefones === 'string' ?
            ((client as any).telefones ? JSON.parse((client as any).telefones) : []) :
            (Array.isArray((client as any).telefones) ? (client as any).telefones : []),
          contatos: typeof (client as any).contatos === 'string' ?
            ((client as any).contatos ? JSON.parse((client as any).contatos) : []) :
            (Array.isArray((client as any).contatos) ? (client as any).contatos : []),
        };
      }),
    retry: false,
    staleTime: 30000, // Cache de 30 segundos para melhorar performance
    refetchOnWindowFocus: false, // Não refetch automático para melhorar performance
    refetchOnMount: true,
  });

  // Logs de debug sempre visíveis
  // Usar clientes reais se disponíveis, senão usar demonstração
  const allClients = realClients.length > 0 ? realClients : demoClients;
  
  const getClientTimestamp = (client: ClientWithHistory) => {
    const rawDate =
      (client as any).created_at ||
      (client as any).createdAt ||
      (client as any).updated_at ||
      (client as any).updatedAt;

    if (!rawDate) return 0;

    const timestamp = new Date(rawDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  // Função para exportar clientes como CSV
  const exportarCSV = () => {
    const headers = ['Nome', 'CPF/CNPJ', 'Telefone', 'Email', 'Endereço', 'Tipo', 'Pedidos', 'Orçamentos', 'Total Gasto'];
    const rows = filteredClients.map(client => [
      client.name || '',
      (client as any).cpf_cnpj || '',
      client.phone || '',
      client.email || '',
      client.address || '',
      client.type || '',
      client.orders || 0,
      client.quotes || 0,
      client.totalValue || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.csv`);
    link.click();
    toast.success('✅ Clientes exportados com sucesso!');
  };

  // Função para determinar status do cliente
  const getClientStatus = (client: ClientWithHistory) => {
    const statuses = [];
    const medidasRegistradas = (client as any).medidas_historico || [];
    const temMedidas = Array.isArray(medidasRegistradas) && medidasRegistradas.length > 0;

    // Verificar se tem medidas
    if (!temMedidas) {
      statuses.push({ tipo: 'sem-medidas', label: '📏 Sem medidas', color: 'bg-amber-100 text-amber-700' });
    }

    // Verificar se tem pedidos
    if ((client.orders || 0) === 0) {
      statuses.push({ tipo: 'sem-pedidos', label: '⚠️ Sem pedidos', color: 'bg-orange-100 text-orange-700' });
    } else if (client.lastOrder) {
      const diasAtrás = Math.floor((Date.now() - new Date(client.lastOrder).getTime()) / (1000 * 60 * 60 * 24));
      if (diasAtrás > 90) {
        statuses.push({ tipo: 'inativo', label: `⏱️ Inativo (${diasAtrás}d)`, color: 'bg-red-100 text-red-700' });
      } else if (diasAtrás > 30) {
        statuses.push({ tipo: 'moderado', label: `⏱️ ${diasAtrás}d atrás`, color: 'bg-yellow-100 text-yellow-700' });
      }
    }

    return statuses;
  };

  // Filtrar clientes pelo termo de busca (nome, telefone, email, CPF/CNPJ, endereço)
  const searchFilteredClients = allClients.filter((client) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase().trim();
    const searchDigitsOnly = searchTerm.replace(/\D/g, '');

    // Buscar em nome, email, endereço (texto normal)
    const matchesText = (
      client.name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.address?.toLowerCase().includes(searchLower)
    );

    // Buscar em telefone e CPF/CNPJ (apenas dígitos)
    const phoneDigitsOnly = client.phone?.replace(/\D/g, '') || '';
    const cpfCnpjDigitsOnly = (client as any).cpf_cnpj?.replace(/\D/g, '') || '';
    const matchesDigits = searchDigitsOnly && (
      phoneDigitsOnly.includes(searchDigitsOnly) ||
      cpfCnpjDigitsOnly.includes(searchDigitsOnly)
    );

    return matchesText || matchesDigits;
  });

  // Aplicar filtros avançados
  const filteredClients = searchFilteredClients.filter((client) => {
    // Filtro por tipo (VIP/Regular)
    if (filterType !== "todos") {
      const clientType = client.type || "Regular";
      if (filterType === "vip" && clientType !== "VIP") return false;
      if (filterType === "regular" && clientType !== "Regular") return false;
    }

    // Filtro por status
    if (filterStatus !== "todos") {
      if (filterStatus === "com-pedidos" && (client.orders || 0) === 0) return false;
      if (filterStatus === "sem-pedidos" && (client.orders || 0) > 0) return false;
      if (filterStatus === "sem-medidas") {
        const medidaRegistradas = (client as any).medidas_historico || [];
        if (Array.isArray(medidaRegistradas) && medidaRegistradas.length > 0) return false;
      }
    }

    return true;
  });

  // Ordenar clientes conforme seleção do usuário
  const allSortedClients = [...filteredClients].sort((a, b) => {
    if (sortBy === "az") {
      return (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" });
    }

    const timeDiff = getClientTimestamp(b) - getClientTimestamp(a);
    if (timeDiff !== 0) {
      return sortBy === "recentes" ? timeDiff : -timeDiff;
    }

    return (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" });
  });

  // Aplicar paginação
  const totalClients = allSortedClients.length;
  const clients = allSortedClients.slice(0, currentPage * itemsPerPage);
  const hasMoreClients = currentPage * itemsPerPage < totalClients;

  // Reset paginação quando busca ou ordenação muda
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K ou Cmd+K para focar na busca
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Nome"]') as HTMLInputElement;
        searchInput?.focus();
      }
      // Ctrl+N ou Cmd+N para novo cliente
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        setIsNovoClienteModalOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Abrir modal automaticamente se veio de um pedido
  useEffect(() => {
    const autoOpenClient = searchParams.get('autoOpenClient');
    if (autoOpenClient && clients.length > 0) {
      const clientToView = clients.find(c =>
        c.name?.toUpperCase() === autoOpenClient.toUpperCase()
      );
      if (clientToView) {
        setViewingClient(clientToView);
        setIsViewDialogOpen(true);
        // Limpar o query param
        setSearchParams({});
      } else {
        // Cliente não encontrado - mostrar dialog para criar
        setClientNotFoundName(autoOpenClient);
        setIsClientNotFoundDialogOpen(true);
        setSearchParams({});
      }
    }
  }, [searchParams, clients, setSearchParams]);

  // Carrega provas do cliente quando abre o modal
  useEffect(() => {
    if (viewingClient?.id) {
      setIsLoadingProvas(true);
      obterProvasDoCliente(viewingClient.id)
        .then(provas => setProvasCliente(provas))
        .catch(err => {
          console.error("Erro ao carregar provas:", err);
          setProvasCliente([]);
        })
        .finally(() => setIsLoadingProvas(false));
    } else {
      setProvasCliente([]);
    }
  }, [viewingClient?.id]);

  // Handlers para abrir modais de pedidos/orçamentos
  const handleOpenOrdersModal = async (client: ClientWithHistory) => {
    setSelectedClientForOrders(client);
    setIsOrdersModalOpen(true);
    setIsLoadingOrders(true);

    try {
      const { data: orders, error } = await supabase
        .from("atelie_orders")
        .select("code, value, paid, status, delivery_date, created_at, customer_name")
        .eq("customer_name", client.name)
        .eq("empresa_id", empresa?.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar pedidos:", error);
        setClientOrders([]);
      } else {
        setClientOrders(orders || []);
      }
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error);
      setClientOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const handleOpenQuotesModal = async (client: ClientWithHistory) => {
    setSelectedClientForQuotes(client);
    setIsQuotesModalOpen(true);
    setIsLoadingQuotes(true);

    try {
      const { data: quotes, error } = await supabase
        .from("atelie_quotes")
        .select("code, total_value, status, date, created_at, customer_name")
        .eq("customer_name", client.name)
        .eq("empresa_id", empresa?.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar orçamentos:", error);
        setClientQuotes([]);
      } else {
        setClientQuotes(quotes || []);
      }
    } catch (error) {
      console.error("Erro ao buscar orçamentos:", error);
      setClientQuotes([]);
    } finally {
      setIsLoadingQuotes(false);
    }
  };

  // Logs que sempre aparecem (usando window.console para garantir)
  useEffect(() => {
    const debugInfo = {
      isLoading,
      queryError: queryError?.message || null,
      realClientsCount: realClients.length,
      realClientsNames: realClients.map(c => c.name || "Sem nome"),
      demoClientsCount: demoClients.length,
      searchTerm,
      allClientsCount: allClients.length,
      clientsCount: clients.length,
      clientsNames: clients.map(c => c.name || "Sem nome")
    };

    // Usar window.console para garantir que não seja removido
    window.console.log("🔍 [CLIENTES] ===== DEBUG INFO =====");
    window.console.log("🔍 [CLIENTES] Debug completo:", JSON.stringify(debugInfo, null, 2));
    window.console.log("🔍 [CLIENTES] isLoading:", isLoading);
    window.console.log("🔍 [CLIENTES] queryError:", queryError);
    window.console.log("🔍 [CLIENTES] realClients.length:", realClients.length);
    window.console.log("🔍 [CLIENTES] realClients:", realClients);
    window.console.log("🔍 [CLIENTES] allClients.length:", allClients.length);
    window.console.log("🔍 [CLIENTES] clients filtrados:", clients.length);
    window.console.log("🔍 [CLIENTES] clientes:", clients.map(c => ({ id: c.id, name: c.name || "Sem nome" })));
    window.console.log("🔍 [CLIENTES] =====================");
  }, [isLoading, queryError, realClients, allClients, clients, searchTerm]);

  // DEBUG TEMPORÁRIO: Expor supabase globalmente para debug do erro 400
  useEffect(() => {
    // @ts-ignore - Temporário para debug
    window.supabase = supabase;
    // @ts-ignore - Temporário para debug
    window.getCurrentEmpresaId = async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('Usuário não logado');

      const { data, error } = await supabase
        .from("user_empresas")
        .select("empresa_id")
        .eq("user_id", user.id)
        .single();

      if (error || !data?.empresa_id) throw new Error('Empresa não encontrada');
      return data.empresa_id;
    };
    // @ts-ignore - Temporário para debug
    window.createCustomer = async (input) => {
      const result = await createCustomer(input);
      return result;
    };

    console.log("🔧 DEBUG: Supabase exposto globalmente em window.supabase");
    console.log("🔧 DEBUG: Funções disponíveis: window.getCurrentEmpresaId, window.createCustomer");
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Clientes"
        description="Gerencie seus clientes"
        icon={<Package className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
        leftActions={
          <>
            <ImportContacts onImportComplete={() => {
              queryClient.invalidateQueries({ queryKey: ["customers"] });
              refetch();
            }} />
            {realClients.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1 md:flex-none text-xs md:text-sm border-red-400 text-red-600 hover:bg-red-50 hover:scale-105 transition-all duration-200 font-medium"
                onClick={handleCleanInvalidCustomers}
                title="Limpar clientes com nomes inválidos (BR, AO, AR, etc.)"
              >
                <Trash className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Limpar Inválidos</span>
                <span className="md:hidden">Limpar</span>
              </Button>
            )}
          </>
        }
        rightActions={
          <PermissionGuard module="clientes" action="create">
            <Button
              size="sm"
              onClick={() => setIsNovoClienteModalOpen(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 hover:scale-105 transition-all duration-200 text-white text-xs md:text-sm font-medium shadow-md"
            >
              <Plus className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden md:inline">Novo Cliente</span>
              <span className="md:hidden">Novo</span>
            </Button>
          </PermissionGuard>
        }
      />

      <div className="px-4 md:px-6 py-3 md:py-4 space-y-4 md:space-y-6">
        <OnboardingChecklist />

        {/* Card informativo de onboarding - Etapa 2 */}
        {onboardingProgress?.shouldShowChecklist && !onboardingProgress?.steps?.some(
          s => s.step === ONBOARDING_STEPS.FIRST_CLIENT && s.completed
        ) && (
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span>📋</span> Etapa 2 de 5: Cadastre seu primeiro cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-700">
                Comece a construir sua carteira de clientes. Clique no botão "Novo Cliente" e preencha os dados básicos.
              </p>
              <div className="flex gap-2 text-xs text-gray-600 bg-white rounded-lg p-2 border border-blue-100">
                <span>✓</span>
                <span>Ao criar o primeiro cliente, a etapa será concluída automaticamente</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <Card className="border-border bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="p-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-600" />
                <Input
                  placeholder="🔍 Nome, telefone, email, CPF/CNPJ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-purple-200 bg-white focus:ring-purple-400"
                />
                {searchTerm && filteredClients.length > 0 && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded">
                    {filteredClients.length} resultado{filteredClients.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  // Limpar completamente o cache
                  queryClient.removeQueries({ queryKey: ["customers"] });
                  queryClient.invalidateQueries({ queryKey: ["customers"] });
                  await refetch();
                  toast.success("Lista de clientes atualizada!");
                }}
                title="Atualizar lista"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "recentes" | "antigos" | "az")}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                title="Ordenar clientes"
              >
                <option value="recentes">Mais recentes</option>
                <option value="antigos">Mais antigos</option>
                <option value="az">A-Z</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={findDuplicates}
                title="Encontrar clientes duplicados"
                className="text-xs"
              >
                🔍 Duplicados
              </Button>
              <Button
                variant={viewMode === "cards" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("cards")}
                className="text-xs"
                title="Visualizar como cards"
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "tabela" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("tabela")}
                className="text-xs"
                title="Visualizar como tabela"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportarCSV}
                className="text-xs"
                title="Exportar para CSV"
              >
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden md:inline">CSV</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Filtros Avançados */}
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-sm font-medium text-muted-foreground">Filtrar por:</span>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                title="Filtrar por tipo de cliente"
              >
                <option value="todos">Todos os tipos</option>
                <option value="vip">⭐ VIP</option>
                <option value="regular">Regular</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                title="Filtrar por status"
              >
                <option value="todos">Todos os status</option>
                <option value="com-pedidos">Com pedidos</option>
                <option value="sem-pedidos">Sem pedidos</option>
                <option value="sem-medidas">Sem medidas</option>
              </select>
              {(filterType !== "todos" || filterStatus !== "todos") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterType("todos");
                    setFilterStatus("todos");
                    setCurrentPage(1);
                  }}
                  className="text-xs text-muted-foreground"
                >
                  ✕ Limpar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Clients Grid */}
        {allSortedClients.length === 0 ? (
          <Card className="border-border">
            <CardContent className="p-8 text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground font-medium mb-2">
                {searchTerm
                  ? `Nenhum cliente encontrado para "${searchTerm}"`
                  : "Nenhum cliente cadastrado ainda"}
              </p>
              {searchTerm && (
                <p className="text-xs text-muted-foreground mb-4">
                  Tente buscar por: nome, telefone, email, CPF/CNPJ ou endereço
                </p>
              )}
              {searchTerm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm("")}
                >
                  Limpar busca
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {realClients.length === 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700 flex items-center gap-2">
                  <span>ℹ️</span>
                  <span><strong>Estes são exemplos</strong> de como aparecerão seus clientes após cadastrar o primeiro.</span>
                </p>
              </div>
            )}

            {/* Selection Header */}
            {clients.length > 0 && (
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border mb-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedClients.size === clients.length && clients.length > 0}
                    onChange={handleSelectAll}
                    className="w-5 h-5 cursor-pointer"
                    title="Selecionar todos"
                  />
                  <span className="text-sm text-muted-foreground">
                    {selectedClients.size > 0
                      ? `${selectedClients.size} cliente(s) selecionado(s)`
                      : "Selecionar todos"}
                  </span>
                </div>

                {selectedClients.size > 0 && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedClients(new Set())}
                      className="text-xs"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleDeleteSelected}
                      disabled={isDeletingSelected}
                      className="text-xs"
                    >
                      {isDeletingSelected ? "Excluindo..." : "Excluir Selecionados"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Exibindo <strong>{clients.length}</strong> de <strong>{totalClients}</strong> clientes
              </div>

              {viewMode === "cards" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {clients.map((client, index) => (
              <Card
              key={index}
              className="border-2 border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 animate-fade-in cursor-pointer rounded-xl overflow-hidden group hover:scale-[1.01]"
            >
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 pb-3">
                  <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 flex-1 pt-0.5">
                    <input
                      type="checkbox"
                      checked={selectedClients.has(client.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectClient(client.id);
                      }}
                      className="w-5 h-5 cursor-pointer mt-1"
                      title="Selecionar cliente"
                    />
                    <div className="w-12 h-12 aspect-square rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform flex-shrink-0">
                      <span className="text-white font-bold text-lg">
                        {client.name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="min-w-0 pt-0.5">
                      <CardTitle className="text-lg font-bold text-foreground group-hover:text-purple-600 transition-colors">
                        {client.name || "Cliente sem nome"}
                      </CardTitle>
                      <div className="flex gap-2 flex-wrap mt-2">
                        <Badge
                          variant="outline"
                          className={
                            client.type === "VIP"
                              ? "bg-yellow-100 text-yellow-700 border-yellow-300 text-xs font-bold px-2 py-1"
                              : "bg-purple-100 text-purple-700 border-purple-300 text-xs font-bold px-2 py-1"
                          }
                        >
                          {client.type}
                        </Badge>
                        {getClientStatus(client).map((status, idx) => (
                          <Badge key={idx} className={`text-xs font-bold px-2 py-1 border-0 ${status.color}`}>
                            {status.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewClient(client);
                      }}
                      className="h-8 w-8 p-0 hover:bg-purple-100 hover:text-purple-600 transition-colors"
                      title="Ver detalhes"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <PermissionGuard module="clientes" action="edit">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClient(client);
                        }}
                        className="h-8 w-8 p-0 hover:bg-purple-100 hover:text-purple-600 transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard module="clientes" action="delete">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClient(client);
                        }}
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-100 hover:text-red-700 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <Phone className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <span className="text-foreground font-medium text-sm">{client.phone}</span>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-pink-600 flex-shrink-0" />
                      <span className="text-foreground font-medium text-sm">{client.email}</span>
                    </div>
                    {client.email && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `mailto:${client.email}`;
                        }}
                        className="h-7 w-7 p-0 hover:bg-pink-200"
                        title="Enviar email"
                      >
                        <Send className="w-3 h-3" />
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg justify-between">
                    <div className="flex items-center gap-3">
                      <MessageCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-foreground font-medium text-sm">{client.phone}</span>
                    </div>
                    {client.phone && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const phoneClean = client.phone.replace(/\D/g, '');
                          window.location.href = `https://wa.me/55${phoneClean}`;
                        }}
                        className="h-7 w-7 p-0 hover:bg-green-200"
                        title="Abrir WhatsApp"
                      >
                        <MessageCircle className="w-3 h-3" />
                      </Button>
                    )}
                  </div>

                  {/* Histórico Real de Pedidos e Orçamentos */}
                  <div className="pt-3 border-t border-gray-200 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 p-3 bg-purple-50 rounded flex-1">
                        <ShoppingCart className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-purple-900">
                        {client.orders} pedidos
                      </span>
                    </div>
                      <div className="flex items-center gap-2 p-3 bg-pink-50 rounded flex-1">
                        <FileText className="w-4 h-4 text-pink-600 flex-shrink-0" />
                        <span className="text-sm font-medium text-pink-900">
                          {client.quotes} orçamentos
                    </span>
                      </div>
                    </div>
                    
                    {client.totalValue > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Total gasto: R$ {client.totalValue.toFixed(2)}
                      </div>
                    )}
                    
                    {(client.lastOrder || client.lastQuote) && (
                      <div className="text-xs text-muted-foreground">
                        Última atividade: {
                          client.lastOrder && client.lastQuote ? 
                            (new Date(client.lastOrder) > new Date(client.lastQuote) ? 
                              `Pedido em ${new Date(client.lastOrder).toLocaleDateString('pt-BR')}` :
                              `Orçamento em ${new Date(client.lastQuote).toLocaleDateString('pt-BR')}`) :
                            client.lastOrder ? 
                              `Pedido em ${new Date(client.lastOrder).toLocaleDateString('pt-BR')}` :
                              `Orçamento em ${new Date(client.lastQuote).toLocaleDateString('pt-BR')}`
                        }
                      </div>
                    )}
                    
                    {/* Links para ver histórico */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                      {client.orders > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenOrdersModal(client);
                          }}
                          className="h-8 text-xs flex-1"
                        >
                          <ShoppingCart className="w-3 h-3 mr-1" />
                          Ver Pedidos
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                      {client.quotes > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenQuotesModal(client);
                          }}
                          className="h-8 text-xs flex-1"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Ver Orçamentos
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
              ))}
              </div>
              ) : (
              <div className="border border-purple-200 rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead className="bg-gradient-to-r from-purple-100 to-pink-100 border-b-2 border-purple-300 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-bold text-purple-900 uppercase">
                        <input
                          type="checkbox"
                          checked={selectedClients.size === clients.length && clients.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 cursor-pointer accent-purple-600"
                        />
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-bold text-purple-900 uppercase whitespace-nowrap">Nome</th>
                      <th className="px-2 py-2 text-left text-xs font-bold text-purple-900 uppercase whitespace-nowrap">Telefone</th>
                      <th className="px-2 py-2 text-left text-xs font-bold text-purple-900 uppercase whitespace-nowrap">Email</th>
                      <th className="px-2 py-2 text-left text-xs font-bold text-purple-900 uppercase whitespace-nowrap">Tipo</th>
                      <th className="px-2 py-2 text-center text-xs font-bold text-purple-900 uppercase whitespace-nowrap">Pedidos</th>
                      <th className="px-2 py-2 text-center text-xs font-bold text-purple-900 uppercase whitespace-nowrap">Orçamentos</th>
                      <th className="px-2 py-2 text-center text-xs font-bold text-purple-900 uppercase whitespace-nowrap">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-purple-100">
                    {clients.map((client, idx) => (
                      <tr
                        key={client.id}
                        className={`transition-all duration-200 text-xs ${
                          idx % 2 === 0 ? 'bg-purple-50/30' : 'bg-white'
                        } hover:bg-gradient-to-r hover:from-purple-100/50 hover:to-pink-100/50 hover:shadow-sm`}
                      >
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={selectedClients.has(client.id)}
                            onChange={() => handleSelectClient(client.id)}
                            className="w-4 h-4 cursor-pointer accent-purple-600"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="space-y-1">
                            <div className="font-bold text-purple-900 whitespace-nowrap">{client.name || "Sem nome"}</div>
                            <div className="flex gap-1 flex-wrap">
                              {getClientStatus(client).map((status, idx) => (
                                <span key={idx} className={`text-xs font-bold px-1.5 py-0.5 rounded-full border ${status.color}`}>
                                  {status.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-between gap-1 whitespace-nowrap">
                            {client.phone || "-"}
                            {client.phone && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const phoneClean = client.phone.replace(/\D/g, '');
                                  window.location.href = `https://wa.me/55${phoneClean}`;
                                }}
                                className="h-6 w-6 p-0 hover:bg-green-200 transition-all flex-shrink-0"
                                title="WhatsApp"
                              >
                                <MessageCircle className="w-3 h-3 text-green-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-between gap-1 whitespace-nowrap">
                            {client.email ? client.email.split('@')[0] : "-"}
                            {client.email && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.location.href = `mailto:${client.email}`}
                                className="h-6 w-6 p-0 hover:bg-pink-200 transition-all flex-shrink-0"
                                title="Email"
                              >
                                <Send className="w-3 h-3 text-pink-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <Badge className={`text-xs font-bold whitespace-nowrap ${
                            client.type === "VIP"
                              ? "bg-yellow-400 text-yellow-900"
                              : "bg-purple-500 text-white"
                          }`}>
                            {client.type}
                          </Badge>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="font-bold text-purple-900 bg-purple-100 px-2 py-0.5 rounded-full whitespace-nowrap inline-block">
                            {client.orders || 0}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="font-bold text-pink-900 bg-pink-100 px-2 py-0.5 rounded-full whitespace-nowrap inline-block">
                            {client.quotes || 0}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center space-x-0.5 flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewClient(client)}
                            className="h-6 w-6 p-0 hover:bg-purple-200 hover:text-purple-600 transition-all"
                            title="Ver"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <PermissionGuard module="clientes" action="edit">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClient(client)}
                              className="h-6 w-6 p-0 hover:bg-blue-200 hover:text-blue-600 transition-all"
                              title="Editar"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                          </PermissionGuard>
                          <PermissionGuard module="clientes" action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClient(client)}
                              className="h-6 w-6 p-0 hover:bg-red-200 hover:text-red-600 transition-all"
                              title="Excluir"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </PermissionGuard>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              </div>
              )}

              {hasMoreClients && (
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  >
                    Carregar mais ({totalClients - clients.length} restantes)
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal de Visualização de Cliente */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-white font-semibold text-lg">
                  {viewingClient?.name?.charAt(0)?.toUpperCase() || "?"}
                </span>
              </div>
              <span>Detalhes do Cliente</span>
            </DialogTitle>
          </DialogHeader>
          {viewingClient && (
            <Tabs defaultValue="basicos" className="w-full">
              <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${(viewingClient as any).tipo_cliente === "pessoa_juridica" ? 7 : 6}, 1fr)` }}>
                <TabsTrigger value="basicos">Básicos</TabsTrigger>
                <TabsTrigger value="contatos">Contatos</TabsTrigger>
                {(viewingClient as any).tipo_cliente === "pessoa_juridica" && <TabsTrigger value="empresa">Empresa</TabsTrigger>}
                <TabsTrigger value="enderecos">Endereços</TabsTrigger>
                <TabsTrigger value="medidas">📏 Medidas</TabsTrigger>
                <TabsTrigger value="provas">👗 Provas</TabsTrigger>
                <TabsTrigger value="observacoes">Observações</TabsTrigger>
              </TabsList>

              {/* ABA 1: BÁSICOS */}
              <TabsContent value="basicos" className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm">Tipo de Cliente</Label>
                    <p className="font-medium capitalize">
                      {(viewingClient as any).tipo_cliente === "pessoa_juridica" ? "Pessoa Jurídica" : "Pessoa Física"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm">Nome</Label>
                    <p className="font-medium">{viewingClient.name || "Cliente sem nome"}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Telefone
                    </Label>
                    <p className="font-medium">{viewingClient.phone || "Não informado"}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </Label>
                    <p className="font-medium">{viewingClient.email || "Não informado"}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm">CPF/CNPJ</Label>
                    <p className="font-medium">
                      {viewingClient.cpf_cnpj ? formatCpfCnpj(viewingClient.cpf_cnpj) : "Não informado"}
                    </p>
                  </div>
                  {(viewingClient as any).data_nascimento && (
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-sm">Data de Nascimento</Label>
                      <p className="font-medium">
                        {new Date((viewingClient as any).data_nascimento).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  )}
                  {viewingClient.address && (
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-muted-foreground text-sm flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Endereço
                      </Label>
                      <p className="font-medium">{viewingClient.address}</p>
                    </div>
                  )}
                  {/* Endereço detalhado */}
                  {(viewingClient.endereco_logradouro || viewingClient.endereco_numero || viewingClient.endereco_complemento || viewingClient.endereco_bairro || viewingClient.endereco_cidade || viewingClient.endereco_uf || viewingClient.endereco_cep) && (
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-muted-foreground text-sm flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Endereço Detalhado
                      </Label>
                      <p className="font-medium">
                        {[
                          viewingClient.endereco_logradouro,
                          viewingClient.endereco_numero,
                          viewingClient.endereco_complemento
                        ].filter(Boolean).join(", ")}
                        {((viewingClient.endereco_bairro || viewingClient.endereco_cidade || viewingClient.endereco_uf || viewingClient.endereco_cep) && ([
                          viewingClient.endereco_bairro,
                          viewingClient.endereco_cidade,
                          viewingClient.endereco_uf,
                          viewingClient.endereco_cep
                        ].filter(Boolean).length > 0)) && (
                          <>
                            <br />
                            {[
                              viewingClient.endereco_bairro,
                              viewingClient.endereco_cidade,
                              viewingClient.endereco_uf,
                              viewingClient.endereco_cep
                            ].filter(Boolean).join(" - ")}
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ABA 2: CONTATOS */}
              <TabsContent value="contatos" className="space-y-4 mt-4">
              {(viewingClient as any).telefones && Array.isArray((viewingClient as any).telefones) && (viewingClient as any).telefones.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Telefones</h3>
                  <div className="space-y-2">
                    {(viewingClient as any).telefones.map((tel: any, idx: number) => (
                      <div key={idx} className="p-2 bg-muted/50 rounded">
                        <p className="text-sm font-medium">{tel.numero || tel} <span className="text-xs text-muted-foreground">({tel.tipo || "principal"})</span></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </TabsContent>

              {/* ABA 3: EMPRESA (APENAS PJ) */}
              {(viewingClient as any).tipo_cliente === "pessoa_juridica" && (<>
              <TabsContent value="empresa" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Informações da Empresa</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(viewingClient as any).inscricao_municipal && (
                      <div className="space-y-1">
                        <Label className="text-muted-foreground text-sm">Inscrição Municipal</Label>
                        <p className="font-medium">{(viewingClient as any).inscricao_municipal}</p>
                      </div>
                    )}
                  </div>

                  {/* Contatos da Empresa */}
                  {(viewingClient as any).contatos && Array.isArray((viewingClient as any).contatos) && (viewingClient as any).contatos.length > 0 && (
                    <div className="space-y-3 mt-4">
                      <h4 className="font-medium text-sm">Contatos</h4>
                      {(viewingClient as any).contatos.map((contato: any, idx: number) => (
                        <div key={idx} className="p-3 bg-muted/50 rounded-lg border-l-2 border-primary">
                          <p className="font-medium text-sm">{contato.nome}</p>
                          <p className="text-xs text-muted-foreground">{contato.telefone} • {contato.cargo || "Sem cargo"}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              </>)}

              {/* ABA 4: ENDEREÇOS */}
              <TabsContent value="enderecos" className="space-y-4 mt-4">
              {((viewingClient as any).endereco_logradouro || (viewingClient as any).endereco_numero) && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Endereço Principal</h3>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Endereço
                    </Label>
                    <p className="font-medium">
                      {[
                        (viewingClient as any).endereco_logradouro,
                        (viewingClient as any).endereco_numero,
                        (viewingClient as any).endereco_complemento
                      ].filter(Boolean).join(", ")}
                      {(((viewingClient as any).endereco_bairro || (viewingClient as any).endereco_cidade || (viewingClient as any).endereco_uf || (viewingClient as any).endereco_cep) && ([
                        (viewingClient as any).endereco_bairro,
                        (viewingClient as any).endereco_cidade,
                        (viewingClient as any).endereco_uf,
                        (viewingClient as any).endereco_cep
                      ].filter(Boolean).length > 0)) && (
                        <>
                          <br />
                          {[
                            (viewingClient as any).endereco_bairro,
                            (viewingClient as any).endereco_cidade,
                            (viewingClient as any).endereco_uf,
                            (viewingClient as any).endereco_cep
                          ].filter(Boolean).join(" - ")}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {((viewingClient as any).endereco_entrega_logradouro || (viewingClient as any).endereco_entrega_numero) && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Endereço de Entrega</h3>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Endereço Diferenciado
                    </Label>
                    <p className="font-medium">
                      {[
                        (viewingClient as any).endereco_entrega_logradouro,
                        (viewingClient as any).endereco_entrega_numero,
                        (viewingClient as any).endereco_entrega_complemento
                      ].filter(Boolean).join(", ")}
                      {(((viewingClient as any).endereco_entrega_bairro || (viewingClient as any).endereco_entrega_cidade || (viewingClient as any).endereco_entrega_uf || (viewingClient as any).endereco_entrega_cep) && ([
                        (viewingClient as any).endereco_entrega_bairro,
                        (viewingClient as any).endereco_entrega_cidade,
                        (viewingClient as any).endereco_entrega_uf,
                        (viewingClient as any).endereco_entrega_cep
                      ].filter(Boolean).length > 0)) && (
                        <>
                          <br />
                          {[
                            (viewingClient as any).endereco_entrega_bairro,
                            (viewingClient as any).endereco_entrega_cidade,
                            (viewingClient as any).endereco_entrega_uf,
                            (viewingClient as any).endereco_entrega_cep
                          ].filter(Boolean).join(" - ")}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Endereço de Cobrança */}
              {((viewingClient as any).endereco_cobranca_logradouro || (viewingClient as any).endereco_cobranca_numero) && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">Endereço de Cobrança</h3>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Endereço Diferenciado
                    </Label>
                    <p className="font-medium">
                      {[
                        (viewingClient as any).endereco_cobranca_logradouro,
                        (viewingClient as any).endereco_cobranca_numero,
                        (viewingClient as any).endereco_cobranca_complemento
                      ].filter(Boolean).join(", ")}
                      {(((viewingClient as any).endereco_cobranca_bairro || (viewingClient as any).endereco_cobranca_cidade || (viewingClient as any).endereco_cobranca_uf || (viewingClient as any).endereco_cobranca_cep) && ([
                        (viewingClient as any).endereco_cobranca_bairro,
                        (viewingClient as any).endereco_cobranca_cidade,
                        (viewingClient as any).endereco_cobranca_uf,
                        (viewingClient as any).endereco_cobranca_cep
                      ].filter(Boolean).length > 0)) && (
                        <>
                          <br />
                          {[
                            (viewingClient as any).endereco_cobranca_bairro,
                            (viewingClient as any).endereco_cobranca_cidade,
                            (viewingClient as any).endereco_cobranca_uf,
                            (viewingClient as any).endereco_cobranca_cep
                          ].filter(Boolean).join(" - ")}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}
              </TabsContent>

              {/* ABA 5: MEDIDAS */}
              <TabsContent value="medidas" className="space-y-4 mt-4">
                <MedidasClienteCard clienteId={viewingClient.id} onRefresh={() => setIsViewDialogOpen(false)} />
              </TabsContent>

              {/* ABA 6: PROVAS */}
              <TabsContent value="provas" className="space-y-4 mt-4">
                {isLoadingProvas ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : provasCliente.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Nenhuma prova agendada ainda</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {provasCliente.map((prova, idx) => (
                      <Card key={idx}>
                        <CardContent className="pt-4">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between">
                              <div className="space-y-1 flex-1">
                                <p className="text-sm font-medium">Pedido: {(prova as any).pedido_numero || "N/A"}</p>
                                <p className="text-xs text-muted-foreground">
                                  {prova.confirmada ? "✅ Confirmada" : "⏳ Aguardando confirmação"}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <div className="text-sm">
                                  <p className="font-medium">{prova.data_prova}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <div className="text-sm">
                                  <p className="font-medium">{prova.hora_prova}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                <div className="text-sm">
                                  <p className="font-medium capitalize">{prova.local_prova}</p>
                                </div>
                              </div>
                            </div>
                            {prova.observacoes && (
                              <div className="bg-blue-50 p-2 rounded text-sm border border-blue-200">
                                <p className="text-blue-900">{prova.observacoes}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ABA 7: OBSERVAÇÕES */}
              <TabsContent value="observacoes" className="space-y-4 mt-4">
              {(viewingClient as any).observacoes && (
                <div className="space-y-2">
                  <p className="text-sm whitespace-pre-wrap">{(viewingClient as any).observacoes}</p>
                </div>
              )}
              {!(viewingClient as any).observacoes && (
                <p className="text-sm text-muted-foreground">Nenhuma observação registrada</p>
              )}
              </TabsContent>
            </Tabs>
          )}

              {/* Ações Rápidas */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    handleEditClient(viewingClient);
                  }}
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Cliente
                </Button>
                {viewingClient && viewingClient.orders > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleOpenOrdersModal(viewingClient);
                    }}
                    className="flex-1"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Ver Pedidos
                  </Button>
                )}
                {viewingClient && viewingClient.quotes > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleOpenQuotesModal(viewingClient);
                    }}
                    className="flex-1"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Ver Orçamentos
                  </Button>
                )}
              </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Duplicados */}
      <Dialog open={isDuplicatesDialogOpen} onOpenChange={setIsDuplicatesDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🔍</span>
              Clientes Duplicados Encontrados
            </DialogTitle>
          </DialogHeader>

          {duplicates && duplicates.length > 0 ? (
            <div className="space-y-6">
              {duplicates.map((group, groupIndex) => (
                <div key={groupIndex} className="border rounded-lg p-4 bg-muted/30">
                  <h3 className="font-semibold text-sm mb-3 text-amber-700 bg-amber-50 p-2 rounded">
                    ⚠️ {group.type}
                  </h3>
                  <div className="space-y-2">
                    {group.clients.map((client) => (
                      <div
                        key={client.id}
                        className="flex items-center justify-between p-3 bg-white border rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{client.name || "Cliente sem nome"}</p>
                          <p className="text-xs text-muted-foreground">
                            ID: {client.id.slice(0, 8)}... | {client.phone || "Sem telefone"}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              handleViewClient(client);
                              setIsDuplicatesDialogOpen(false);
                            }}
                            className="text-xs"
                          >
                            Ver
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              handleSelectClient(client.id);
                            }}
                            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Marcar para Excluir
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  💡 <strong>Dica:</strong> Você pode marcar os clientes duplicados e usar a função "Excluir Selecionados" acima
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Nenhum duplicado encontrado</p>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDuplicatesDialogOpen(false)}
            >
              Fechar
            </Button>
            {duplicates && duplicates.length > 0 && (
              <Button
                onClick={handleAutoCleanupDuplicates}
                disabled={isDeletingSelected}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeletingSelected ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Limpando...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Limpar Duplicados Automaticamente
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para cliente não encontrado */}
      <Dialog open={isClientNotFoundDialogOpen} onOpenChange={setIsClientNotFoundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cliente não encontrado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              O cliente <strong>{clientNotFoundName}</strong> não está cadastrado no seu sistema.
            </p>
            <p className="text-sm text-muted-foreground">
              Deseja criar um novo cliente com este nome?
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsClientNotFoundDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setIsClientNotFoundDialogOpen(false);
                setIsNovoClienteModalOpen(true);
                setSearchTerm(clientNotFoundName || "");
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Criar Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Pedidos do Cliente */}
      <Dialog open={isOrdersModalOpen} onOpenChange={setIsOrdersModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-purple-600" />
              Pedidos de {selectedClientForOrders?.name}
            </DialogTitle>
          </DialogHeader>

          {isLoadingOrders ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : clientOrders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum pedido encontrado para este cliente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clientOrders.map((order: any, idx: number) => (
                <div
                  key={idx}
                  className="border rounded-lg p-4 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer group"
                  onClick={() => {
                    setIsOrdersModalOpen(false);
                    navigate(`/pedidos/${order.code}`);
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-bold text-lg text-purple-900 group-hover:text-purple-600">
                        Pedido #{order.code}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Badge
                      className={`text-xs font-bold whitespace-nowrap ${
                        order.status === 'concluído' || order.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : order.status === 'em progresso' || order.status === 'processing'
                          ? 'bg-blue-100 text-blue-700'
                          : order.status === 'cancelado' || order.status === 'cancelled'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {order.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground text-xs">Valor</span>
                      <p className="font-semibold text-purple-900">
                        R$ {(order.value || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">Pagamento</span>
                      <p className="font-semibold">
                        {order.paid ? (
                          <span className="text-green-600">✓ Pago</span>
                        ) : (
                          <span className="text-orange-600">⏳ Pendente</span>
                        )}
                      </p>
                    </div>
                    {order.delivery_date && (
                      <div>
                        <span className="text-muted-foreground text-xs">Entrega</span>
                        <p className="font-semibold">
                          {new Date(order.delivery_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pedidos?search=${order.code}`);
                        setIsOrdersModalOpen(false);
                      }}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                    >
                      Ver Detalhes
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsOrdersModalOpen(false)}
            >
              Fechar
            </Button>
            <Button
              onClick={() => {
                setIsOrdersModalOpen(false);
                navigate('/pedidos');
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Ver Todos os Pedidos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Orçamentos do Cliente */}
      <Dialog open={isQuotesModalOpen} onOpenChange={setIsQuotesModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-pink-600" />
              Orçamentos de {selectedClientForQuotes?.name}
            </DialogTitle>
          </DialogHeader>

          {isLoadingQuotes ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : clientQuotes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum orçamento encontrado para este cliente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clientQuotes.map((quote: any, idx: number) => (
                <div
                  key={idx}
                  className="border rounded-lg p-4 hover:border-pink-400 hover:bg-pink-50 transition-all cursor-pointer group"
                  onClick={() => {
                    setIsQuotesModalOpen(false);
                    navigate(`/orcamentos/${quote.code}/visualizar`);
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-bold text-lg text-pink-900 group-hover:text-pink-600">
                        Orçamento #{quote.code}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(quote.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Badge
                      className={`text-xs font-bold whitespace-nowrap ${
                        quote.status === 'aprovado' || quote.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : quote.status === 'pendente' || quote.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : quote.status === 'recusado' || quote.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {quote.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground text-xs">Valor Total</span>
                      <p className="font-semibold text-pink-900">
                        R$ {(quote.total_value || 0).toFixed(2)}
                      </p>
                    </div>
                    {quote.date && (
                      <div>
                        <span className="text-muted-foreground text-xs">Data do Orçamento</span>
                        <p className="font-semibold">
                          {new Date(quote.date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/orcamentos?search=${quote.code}`);
                        setIsQuotesModalOpen(false);
                      }}
                      className="text-pink-600 hover:text-pink-700 hover:bg-pink-100"
                    >
                      Ver Detalhes
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsQuotesModalOpen(false)}
            >
              Fechar
            </Button>
            <Button
              onClick={() => {
                setIsQuotesModalOpen(false);
                navigate('/orcamentos');
              }}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              Ver Todos os Orçamentos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditClienteModal
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        client={editingClient}
        onboardingProgress={onboardingProgress}
        refetch={refetch}
      />

      <NovoClienteModal
        open={isNovoClienteModalOpen}
        onOpenChange={setIsNovoClienteModalOpen}
        onboardingProgress={onboardingProgress}
        refetch={refetch}
      />
    </div>
  );
}
